// Teach once (U7): the API shapes for widening one correction to a sender, a domain, or a sender
// and subject. Shared by the server and the web (no runtime deps beyond the rule types).
import type { Verdict } from "./measure";
import type { Destination } from "./routing";
import type { RuleScope, SenderRule } from "./senderRules";

/** "once" changes nothing beyond the action just taken. */
export type TeachScope = "once" | RuleScope;

export const TEACH_SCOPES: readonly TeachScope[] = ["once", "sender", "domain", "sender_subject"];

/** Where the chooser was offered. */
export type TeachSurface = "dismiss" | "restore" | "verdict";

export interface TeachOption {
  scope: TeachScope;
  /** "Just this once", "This sender", "This domain", "This sender and subject". */
  label: string;
  /** The concrete people or words it covers: "news@shop.com", "anyone at shop.com". */
  detail: string;
  /** Why it can't be offered (a shared mail domain such as gmail.com); null when it can. */
  unavailable: string | null;
}

/** GET /api/teach/options?threadKey= */
export interface TeachOptionsView {
  threadKey: string;
  sender: { address: string; name: string | null } | null;
  subject: string | null;
  options: TeachOption[];
}

/** POST /api/teach */
export interface TeachRequest {
  threadKey: string;
  scope: Exclude<TeachScope, "once">;
  handling: Destination;
  surface: TeachSurface;
  cardId?: string;
}

/** One thread a rule would have touched in the last 30 days. */
export interface DryRunRow {
  threadKey: string;
  subject: string | null;
  from: string | null;
  at: string;
  /** "ok" (✔): handled as the rule intends; "miss" (✖): something says otherwise. */
  mark: "ok" | "miss";
  /** Plain words: what the rule would do, or why it looks wrong for this one. */
  why: string;
}

/** GET /api/teach/proposals/:id/dry-run */
export interface DryRunView {
  proposalId: string;
  rule: SenderRule;
  /** Start of the window looked at (30 days back). */
  since: string;
  rows: DryRunRow[];
  /** Matching threads beyond the rows shown (oldest left out). */
  more: number;
}

/** POST /api/teach/proposals/:id/refine */
export interface RefineRequest {
  threadKey: string;
}

/** What a wider scope would do by default, from where the chooser was offered. */
export function defaultHandling(surface: TeachSurface, verdict?: Verdict | null): Destination {
  if (surface === "restore") return "cards";
  if (surface === "verdict" && verdict === "should_be_card") return "cards";
  return "brief";
}
